import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Search, Bell, ChevronDown, Plus, Check, CheckCircle2,
  AlertCircle, LayoutDashboard, Receipt, TrendingUp, FileText,
  CreditCard, Wallet, Target, RepeatIcon, CheckSquare, Bot,
  Settings, UserCog, LogOut, Sun, Moon, ExternalLink, X, CalendarDays, ArrowLeftRight, Gauge
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { resolveDashboardCards } from '../utils/dashboardCards';
import { useTheme } from '../contexts/ThemeContext';
import ProfileModal from './ProfileModal';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/format';
import calendarGastifyIcon from '../assets/calendar-gastify.png';

const CalendarGastifySearchIcon = ({ size = 16, className = '' }) => (
  <img
    src={calendarGastifyIcon}
    alt="Recorrências"
    className={`object-contain flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
  />
);

export default function HeaderBar({ title = 'Dashboard', onNovoLancamento }) {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenantSlug } = useParams();
  const navigate = useNavigate();

  // Estados de Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Estados de Notificações
  const [notifOpen, setNotifOpen] = useState(false);
  const [changelogs, setChangelogs] = useState([]);
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('gastify_read_changelogs') || '[]');
    } catch {
      return [];
    }
  });
  const notifContainerRef = useRef(null);

  // Estados de Menu do Usuário
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Rotas do sistema para pesquisa rápida
  const systemPages = [
    { title: 'Dashboard', path: `/${tenantSlug}/dashboard`, icon: LayoutDashboard, category: 'Navegação', keywords: 'inicio home painel kpi' },
    { title: 'Lançamentos', path: `/${tenantSlug}/lancamentos`, icon: Receipt, category: 'Navegação', keywords: 'extrato receitas despesas transacoes' },
    { title: 'Contas a Pagar / Receber', path: `/${tenantSlug}/contas-pagar`, icon: CreditCard, category: 'Navegação', keywords: 'vencimentos boletos atrasados pendentes pagar' },
    { title: 'Fluxo de Caixa', path: `/${tenantSlug}/fluxo-caixa`, icon: TrendingUp, category: 'Navegação', keywords: 'projecao historico caixa entradas saidas' },
    { title: 'Calendário', path: `/${tenantSlug}/calendario`, icon: CalendarDays, category: 'Navegação', keywords: 'agenda vencimentos datas mes faturas' },
    { title: 'Importar e Exportar', path: `/${tenantSlug}/importar-exportar`, icon: ArrowLeftRight, category: 'Navegação', keywords: 'csv excel pdf planilha importar exportar backup' },
    { title: 'Meu Desempenho', path: `/${tenantSlug}/desempenho`, icon: Gauge, category: 'Navegação', keywords: 'saude financeira pontuacao economia poupanca evolucao gastos' },
    { title: 'Relatórios', path: `/${tenantSlug}/relatorios`, icon: FileText, category: 'Navegação', keywords: 'dre drex exportar pdf excel demonstrativo' },
    { title: 'Carteira & Contas', path: `/${tenantSlug}/carteira`, icon: Wallet, category: 'Navegação', keywords: 'bancos saldo caixas cartoes' },
    { title: 'Metas Financeiras', path: `/${tenantSlug}/metas`, icon: Target, category: 'Navegação', keywords: 'objetivos economia progresso poupanca' },
    { title: 'Recorrências', path: `/${tenantSlug}/recorrencias`, icon: CalendarGastifySearchIcon, category: 'Navegação', keywords: 'assinaturas fixas mensais recorrente' },
    { title: 'Tarefas & Notas', path: `/${tenantSlug}/tarefas`, icon: CheckSquare, category: 'Navegação', keywords: 'lembretes anotacoes to-do afazeres' },
    { title: 'Assistente IA', path: `/${tenantSlug}/chat`, icon: Bot, category: 'Navegação', keywords: 'ia claude chat financeiro analise' },
    { title: 'Configurações', path: `/${tenantSlug}/configuracoes`, icon: Settings, category: 'Navegação', keywords: 'sistema empresa categorias personalizacao' },
  ].filter(pg => !pg.path.endsWith('/fluxo-caixa') || resolveDashboardCards(user).menu_fluxo_caixa);

  // Fechar dropdowns com click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (notifContainerRef.current && !notifContainerRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Atalho global Ctrl+K / Cmd+K para focar a barra de pesquisa
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNotifOpen(false);
        setUserMenuOpen(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Carregar changelog para notificações
  useEffect(() => {
    api.get('/changelog')
      .then(res => {
        if (res.data?.items) {
          setChangelogs(res.data.items);
        }
      })
      .catch(() => {});
  }, []);

  // Quantidade de notificações não lidas
  const unreadCount = changelogs.filter(c => !readIds.includes(c.id)).length;

  const handleMarkAllRead = () => {
    const allIds = changelogs.map(c => c.id);
    setReadIds(allIds);
    localStorage.setItem('gastify_read_changelogs', JSON.stringify(allIds));
  };

  const handleMarkSingleRead = (id) => {
    if (!readIds.includes(id)) {
      const updated = [...readIds, id];
      setReadIds(updated);
      localStorage.setItem('gastify_read_changelogs', JSON.stringify(updated));
    }
  };

  // Busca debounced
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.data);
      } catch (err) {
        console.error('Erro na busca global:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 220);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Filtra páginas do sistema localmente
  const matchedPages = searchQuery.trim()
    ? systemPages.filter(p => {
        const term = searchQuery.toLowerCase();
        return (
          p.title.toLowerCase().includes(term) ||
          p.keywords.toLowerCase().includes(term)
        );
      })
    : [];

  const handleNavigate = (path) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(path);
  };

  const hasAnyResults =
    matchedPages.length > 0 ||
    (searchResults &&
      (searchResults.lancamentos?.length > 0 ||
        searchResults.contasPagar?.length > 0 ||
        searchResults.carteiras?.length > 0 ||
        searchResults.metas?.length > 0 ||
        searchResults.recorrencias?.length > 0));

  const initial = (user?.nomeCompleto || user?.username || 'A')[0].toUpperCase();

  return (
    <>
      <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
        {/* Título da tela */}
        <div>
          <h1
            className="text-[22px] sm:text-[26px] font-bold tracking-tight text-slate-800 dark:text-slate-100"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {title}
          </h1>
        </div>

        {/* Barra superior de ações */}
        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          {/* 1. BARRA DE PESQUISA */}
          <div className="relative" ref={searchContainerRef}>
            <div className="relative flex items-center">
              <Search
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Pesquisar..."
                value={searchQuery}
                onFocus={() => setSearchOpen(true)}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                className="pl-8 sm:pl-9 pr-7 sm:pr-9 py-2 bg-white dark:bg-slate-900 rounded-full text-xs font-medium text-slate-700 dark:text-slate-200 placeholder-slate-400 border border-white dark:border-slate-800 shadow-[0_4px_14px_rgba(112,144,176,0.12)] focus:outline-none focus:ring-2 focus:ring-slate-300/60 w-32 xs:w-40 sm:w-64 transition-all"
                data-testid="header-search-input"
              />
              {searchQuery ? (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults(null);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                >
                  <X size={13} />
                </button>
              ) : (
                <span className="hidden md:inline-block absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.2 pointer-events-none">
                  Ctrl K
                </span>
              )}
            </div>

            {/* Dropdown de Resultados da Pesquisa */}
            {searchOpen && searchQuery.trim().length > 0 && (
              <div
                className="absolute right-0 sm:left-0 sm:right-auto mt-2 w-[calc(100vw-32px)] sm:w-[440px] max-w-[440px] bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-[0_12px_36px_rgba(15,23,42,0.14)] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
                data-testid="header-search-dropdown"
              >
                {/* Header da Busca */}
                <div className="px-4 py-2.5 bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
                  <span>
                    Buscando por: <strong className="text-slate-800 dark:text-slate-200">"{searchQuery}"</strong>
                  </span>
                  {searchLoading && <span className="animate-pulse text-blue-600">Pesquisando...</span>}
                </div>

                <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60">
                  {/* Seção de Páginas / Menus */}
                  {matchedPages.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Navegação rápida
                      </p>
                      {matchedPages.slice(0, 4).map(p => {
                        const Icon = p.icon;
                        return (
                          <button
                            key={p.path}
                            onClick={() => handleNavigate(p.path)}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors group"
                          >
                            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                              <Icon size={14} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                {p.title}
                              </p>
                              <p className="text-[10px] text-slate-400 truncate">Ir para {p.title}</p>
                            </div>
                            <ExternalLink size={12} className="text-slate-300 group-hover:text-slate-500" />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Seção de Lançamentos */}
                  {searchResults?.lancamentos?.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                        <span>Lançamentos Encontrados</span>
                        <span className="text-[10px] font-normal text-slate-400">
                          {searchResults.lancamentos.length}
                        </span>
                      </p>
                      {searchResults.lancamentos.map(l => {
                        const isReceita = l.tipo === 'RECEITA';
                        const colorClass = isReceita ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40' : 'text-rose-600 bg-rose-50 dark:bg-rose-950/40';
                        return (
                          <button
                            key={l.id}
                            onClick={() => handleNavigate(`/${tenantSlug}/lancamentos?busca=${encodeURIComponent(l.descricao || '')}`)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors group"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                {l.descricao}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                                <span>{l.categoria || 'Geral'}</span>
                                <span>•</span>
                                <span>{formatDate(l.dataCompetencia || l.dataPagamento)}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${colorClass}`}>
                                {isReceita ? '+' : '-'} {formatCurrency(l.valor)}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Seção de Contas a Pagar / Pendentes */}
                  {searchResults?.contasPagar?.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                        <span>Contas a Pagar / Pendentes</span>
                        <span className="text-[10px] font-normal text-slate-400">
                          {searchResults.contasPagar.length}
                        </span>
                      </p>
                      {searchResults.contasPagar.map(c => (
                        <button
                          key={c.id}
                          onClick={() => handleNavigate(`/${tenantSlug}/contas-pagar?busca=${encodeURIComponent(c.descricao || '')}`)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors group"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                {c.descricao}
                              </p>
                              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.2 rounded ${c.status === 'atrasado' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {c.status}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              Vencimento: {formatDate(c.dataCompetencia)}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                              {formatCurrency(c.valor)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Seção de Carteiras */}
                  {searchResults?.carteiras?.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Carteiras
                      </p>
                      {searchResults.carteiras.map(cart => (
                        <button
                          key={cart.id}
                          onClick={() => handleNavigate(`/${tenantSlug}/carteira`)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Wallet size={14} className="text-slate-400 flex-shrink-0" />
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {cart.nome}
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-500 uppercase">{cart.tipo || 'Conta'}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Seção de Metas */}
                  {searchResults?.metas?.length > 0 && (
                    <div className="p-2">
                      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Metas Financeiras
                      </p>
                      {searchResults.metas.map(meta => (
                        <button
                          key={meta.id}
                          onClick={() => handleNavigate(`/${tenantSlug}/metas`)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Target size={14} className="text-slate-400 flex-shrink-0" />
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {meta.titulo}
                            </span>
                          </div>
                          <span className="text-[11px] text-emerald-600 font-bold">{formatCurrency(meta.valorAlvo)}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Caso não encontre nenhum resultado */}
                  {!hasAnyResults && !searchLoading && (
                    <div className="py-8 px-4 text-center">
                      <AlertCircle size={24} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        Nenhum resultado encontrado
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1 max-w-xs mx-auto">
                        Não encontramos registros para "{searchQuery}". Tente buscar por outros termos, datas ou categorias.
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer do Dropdown */}
                <div className="p-2 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-400">
                  <span>Pressione Enter para ver tudo</span>
                  <span>ESC para fechar</span>
                </div>
              </div>
            )}
          </div>

          {/* 2. ÍCONE DE NOTIFICAÇÕES (CHANGELOG PARA O CLIENTE) */}
          <div className="relative" ref={notifContainerRef}>
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="relative w-9 h-9 rounded-2xl bg-white dark:bg-slate-900 border border-white dark:border-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 shadow-[0_4px_14px_rgba(112,144,176,0.12)] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer"
              aria-label="Notificações"
              data-testid="header-notifications-btn"
            >
              <Bell size={16} />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900 animate-pulse" />
              )}
            </button>

            {/* Painel de Notificações / Changelog */}
            {notifOpen && (
              <div
                className="absolute right-0 mt-2 w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px] bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-[0_12px_36px_rgba(15,23,42,0.14)] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
                data-testid="header-notifications-panel"
              >
                {/* Header do Painel */}
                <div className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-slate-900 dark:bg-slate-800 p-1.5 flex items-center justify-center shadow-xs border border-slate-200/80 dark:border-white/10 flex-shrink-0">
                      <img src="/gastify-icon-32.png" alt="Gastify" className="w-full h-full object-contain rounded-md" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                        Novidades do Sistema
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/50">
                          Gastify
                        </span>
                      </h3>
                      <p className="text-[10px] text-slate-400">
                        {unreadCount > 0 ? `${unreadCount} nova(s) atualização(ões)` : 'Tudo atualizado'}
                      </p>
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-colors"
                      title="Marcar todas como lidas"
                    >
                      <Check size={12} />
                      <span>Marcar lidas</span>
                    </button>
                  )}
                </div>

                {/* Lista de Changelog / Notificações */}
                <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                  {changelogs.length > 0 ? (
                    changelogs.map(item => {
                      const isRead = readIds.includes(item.id);
                      return (
                        <div
                          key={item.id}
                          onClick={() => handleMarkSingleRead(item.id)}
                          className={`p-3.5 rounded-xl transition-all cursor-pointer ${
                            isRead
                              ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50 opacity-80'
                              : 'bg-blue-50/40 dark:bg-blue-950/20 hover:bg-blue-50/70'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                                {item.versao}
                              </span>
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                                  item.tipo === 'novidade'
                                    ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                                    : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                                }`}
                              >
                                {item.tag || (item.tipo === 'novidade' ? 'Novidade' : 'Melhoria')}
                              </span>
                            </div>
                            <span className="text-[10px] text-slate-400">{formatDate(item.data)}</span>
                          </div>

                          <h4 className="text-xs font-bold text-slate-800 dark:text-slate-100 mb-1 leading-snug">
                            {item.titulo}
                          </h4>
                          <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                            {item.descricao}
                          </p>

                          {!isRead && (
                            <div className="flex items-center justify-end mt-2">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-rose-500 bg-rose-50 dark:bg-rose-950 px-1.5 py-0.5 rounded">
                                Não lido
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-slate-400">
                      <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-500" />
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        Nenhuma notificação pendente
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Você já está na versão mais recente do Gastify.
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer do Changelog */}
                <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 text-center text-[10px] text-slate-400 font-medium">
                  Gastify Sistema • Versão 2.4.0
                </div>
              </div>
            )}
          </div>

          {/* 3. MENU DO USUÁRIO (AVATAR COM "A" E SETINHA) */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-1.5 p-1 bg-white dark:bg-slate-900 rounded-full border border-white dark:border-slate-800 shadow-[0_4px_14px_rgba(112,144,176,0.12)] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer select-none"
              aria-label="Menu do Usuário"
              data-testid="header-user-menu-btn"
            >
              {user?.fotoPerfil ? (
                <img
                  src={user.fotoPerfil}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover border border-white dark:border-slate-700"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-800 dark:bg-slate-700 text-white flex items-center justify-center text-xs font-bold uppercase shadow-sm">
                  {initial}
                </div>
              )}
              <ChevronDown
                size={14}
                className={`text-slate-400 pr-1 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown de Administrador e Meu Perfil */}
            {userMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-32px)] bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-[0_12px_36px_rgba(15,23,42,0.14)] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
                data-testid="header-user-dropdown"
              >
                {/* Header do Perfil com Avatar e Nome */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    {user?.fotoPerfil ? (
                      <img
                        src={user.fotoPerfil}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover border border-white dark:border-slate-700 shadow-sm"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center text-sm font-bold uppercase shadow-sm">
                        {initial}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                        {user?.nomeCompleto || user?.username || 'Administrador'}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        @{user?.username || 'admin'}
                      </p>
                      <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.2 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                        Administrador
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ações do Menu */}
                <div className="p-1.5 space-y-0.5">
                  {/* Meu Perfil (abre o modal de edição de perfil/foto/senha) */}
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      setProfileModalOpen(true);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors group cursor-pointer"
                    data-testid="header-open-profile-btn"
                  >
                    <UserCog size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold">Meu perfil</p>
                      <p className="text-[10px] text-slate-400">Editar foto, dados e senha</p>
                    </div>
                  </button>

                  {/* Configurações */}
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate(`/${tenantSlug}/configuracoes`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors group cursor-pointer"
                    data-testid="header-open-settings-btn"
                  >
                    <Settings size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold">Configurações</p>
                      <p className="text-[10px] text-slate-400">Preferências e dados da empresa</p>
                    </div>
                  </button>

                  {/* Alternar Tema - visível apenas no mobile (lg:hidden), no desktop fica no sidebar esquerdo */}
                  <div className="block lg:hidden">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTheme();
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors group cursor-pointer"
                      data-testid="header-theme-toggle-btn"
                    >
                      <div className="flex items-center gap-3">
                        {theme === 'dark' ? (
                          <Moon size={16} className="text-indigo-400" />
                        ) : (
                          <Sun size={16} className="text-amber-500" />
                        )}
                        <span className="text-xs font-semibold">Modo {theme === 'dark' ? 'Escuro' : 'Claro'}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 uppercase font-medium">Alternar</span>
                    </button>
                  </div>
                </div>

                {/* Sair da Conta - visível apenas no mobile (lg:hidden), no desktop fica no sidebar esquerdo */}
                <div className="block lg:hidden p-1.5 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                      navigate(`/${tenantSlug}/login`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors cursor-pointer"
                    data-testid="header-logout-btn"
                  >
                    <LogOut size={15} />
                    <span className="text-xs font-semibold">Sair da conta</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 4. BOTÃO NOVO LANÇAMENTO (SE FORNECIDO) */}
          {onNovoLancamento && (
            <button
              onClick={onNovoLancamento}
              className="hidden sm:flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3.5 py-2 rounded-2xl shadow-md transition-all active:scale-[0.98] cursor-pointer"
              data-testid="dashboard-novo-lancamento-btn"
            >
              <Plus size={14} />
              <span>Novo</span>
            </button>
          )}
        </div>
      </div>

      {/* Modal de Perfil acionado pelo Meu Perfil */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        kind="tenant"
        currentUser={user}
        onSuccess={refreshUser}
      />
    </>
  );
}
